import { Controller } from 'stimulus'
import { useClickOutside, useDebounce } from 'stimulus-use'
import { inViewPortOfContainer } from '~/common/helpers/in_viewport'
import { throttle } from 'throttle-debounce'
import animateScrollTo from 'animated-scroll-to'
import tippy, { roundArrow } from 'tippy.js'
import $ from 'jquery'
import {
  setPlayerCurrentTime,
  waitForPlayer,
} from '~/common/components/videojs/helpers'
import { getCsrfParamsAsObject } from '~/common/helpers/csrf'
import { haptics } from '~/common/helpers/haptics'
import './asset_transcript_component.css'
import { getScrollParent } from '~/common/helpers/scroll_parent'
import {
  AUTO_FOLLOW_TOP_OFFSET,
  isSentenceInFollowView,
  returnControlState,
} from './asset_transcript_component_helpers.js'

export default class extends Controller {
  static classes = [
    'highlightedSentence',
    'editing',
    'searchMatch',
    'userHighlight',
  ]

  static targets = [
    'transcriptContainer',
    'header',
    'scrollToCurrentPositionButton',
    'scrollToCurrentPositionIcon',
    'buttons',
    'searchInput',
    'clearSearchButton',
    'searchInputContainer',
    'searchCount',
    'searchNavigation',
    'highlightsOnlyButton',
    'bookmarksOnlyButton',
    'userHighlightTooltip',
    'bookmarkTooltip',
    'userBookmarkTemplate',
    'breakMarkerTemplate',
    'selectionHandleTemplate',
  ]

  static values = {
    playerSelector: String,
    linkUrl: String,
    annotateUrl: String,
    updateTranscriptUrl: String,
    assetId: Number,
    contextType: String,
    contextId: Number,
    alwaysSearch: Boolean,
    noMatches: String,
  }

  static debounces = [
    'search',
    { name: 'doSearch', wait: 1 },
    { name: 'scrollToSentence', wait: 1 },
  ]

  // A class field, not reset on reconnect, so an animation still in flight from
  // an earlier connection can never match a token issued by a later one.
  scrollToken = 0

  // Auto-follow never runs while search is active, so it is never enabled then.
  // Writers record whether the member wants following; readers get whether it is
  // actually on.
  get shouldAutoScroll() {
    return this._shouldAutoScroll && !this.searchActive
  }

  set shouldAutoScroll(value) {
    this._shouldAutoScroll = value
  }

  connect() {
    useDebounce(this)
    this.highlightedMatchNum = 0
  }

  // LocalStorage helpers for non-logged-in users
  isUserLoggedIn() {
    return this.annotateUrlValue && this.annotateUrlValue !== ''
  }

  getLocalStorageKey() {
    return `transcript_highlights_${this.assetIdValue}`
  }

  getLocalHighlights() {
    try {
      const key = this.getLocalStorageKey()
      const data = localStorage.getItem(key)
      return data ? JSON.parse(data) : []
    } catch (e) {
      console.error('Error reading local highlights:', e)
      return []
    }
  }

  saveLocalHighlights(highlights) {
    const key = this.getLocalStorageKey()
    localStorage.setItem(key, JSON.stringify(highlights))
  }

  transcriptContainerTargetConnected() {
    this.initializeWidget()
  }

  initializeWidget = async () => {
    this.widgetInitialized = true
    this.scrollContainer = getScrollParent(this.element)
    this.addEventListeners()
    this.shouldAutoScroll = true
    this.searchActive = this.alwaysSearchValue
    if (this.alwaysSearchValue) this.element.classList.add('search-active')
    this.transcriptHeaderHidden = false
    this.lastScrollTop = this.scrollContainer?.scrollTop || 0
    this.headerToggleLockUntil = 0
    this.headerVisibilityDirection = null
    this.headerDirectionAnchorScrollTop = this.lastScrollTop
    this.isScrolling = false
    this.numMatches = 0
    this.player = await waitForPlayer(
      document.querySelector(this.playerSelectorValue)
    )
    this.sentences = Array.from(
      this.element.querySelectorAll('.js-sentence')
    ).map((el) => ({
      start: parseFloat(el.dataset.start),
      end: parseFloat(el.dataset.end),
      el,
    }))
    this.player.on('timeupdate', this.onTimeUpdate)

    // Highlight saved position immediately (before playback starts)
    this.highlightSavedPosition()

    // Load localStorage highlights if not logged in
    if (!this.isUserLoggedIn()) {
      this.loadLocalHighlights()
    } else {
      // If user IS logged in, check if there are any localStorage highlights to sync
      this.syncLocalHighlightsIfNeeded()
    }
  }

  loadLocalHighlights() {
    const highlights = this.getLocalHighlights()

    highlights.forEach((highlight) => {
      const sentences = this.sentences.filter(
        (s) => s.start >= highlight.start_pos && s.end <= highlight.end_pos
      )

      sentences.forEach((sentence) => {
        sentence.el.classList.add(...this.userHighlightClasses)
        sentence.el.dataset.deleteUrl = 'local'
      })
    })
  }

  syncLocalHighlightsIfNeeded() {
    const allHighlights = []
    const keysToDelete = []

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      if (key && key.startsWith('transcript_highlights_')) {
        const assetId = parseInt(key.replace('transcript_highlights_', ''))

        try {
          const highlights = JSON.parse(localStorage.getItem(key))

          if (Array.isArray(highlights) && highlights.length > 0) {
            highlights.forEach((h) => {
              allHighlights.push({
                asset_id: assetId,
                // Fallback to start_sec/end_sec for old localStorage entries (pre-rename)
                start_pos: h.start_pos || h.start_sec,
                end_pos: h.end_pos || h.end_sec,
                context_type: h.context_type,
                context_id: h.context_id,
              })
            })
            keysToDelete.push(key)
          }
        } catch (e) {
          console.error(`Failed to parse highlights for ${key}:`, e)
        }
      }
    }

    if (allHighlights.length === 0) return

    fetch('/member_asset_annotations/sync_local', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...getCsrfParamsAsObject(),
        highlights: allHighlights,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.errors?.length > 0) {
          console.warn('Some highlights failed:', data.errors)
        }

        // Clear localStorage and apply fresh highlights if any exist
        if (Object.keys(data.highlights || {}).length > 0) {
          keysToDelete.forEach((key) => localStorage.removeItem(key))
          this.applyFreshHighlights(data.highlights)
        }
      })
      .catch((error) => {
        console.error('Sync failed:', error)
      })
  }

  applyFreshHighlights(highlightsByAsset) {
    const highlights = highlightsByAsset[this.assetIdValue.toString()] || []

    highlights.forEach((highlight) => {
      const sentences = this.sentences.filter(
        (s) => s.start >= highlight.start_pos && s.end <= highlight.end_pos
      )

      sentences.forEach((sentence) => {
        // Only add if not already highlighted
        if (!sentence.el.classList.contains(this.userHighlightClasses[0])) {
          sentence.el.classList.add(...this.userHighlightClasses)
          sentence.el.dataset.deleteUrl = highlight.delete_url
        }
      })
    })
  }

  disconnect() {
    if (!this.widgetInitialized) return
    this.player?.off('timeupdate', this.onTimeUpdate)
    this.removeEventListeners()
    clearTimeout(this.savedPositionScrollTimeout)
    clearTimeout(this.shouldAutoScrollTimeout)
    clearTimeout(this._longPressTimer)
    this.scrollToken++
    this._removeHandles()
    document.removeEventListener('touchmove', this._onHandleTouchMove)
    document.removeEventListener('touchend', this._onHandleTouchEnd)
    document.removeEventListener('touchcancel', this._onHandleTouchEnd)
    this._activeHandle = null
    this.scrollContainer = null
    this.widgetInitialized = false
  }

  addEventListeners() {
    this.scrollContainer.addEventListener('wheel', this.onScroll)
    this.scrollContainer.addEventListener('scroll', this.onScroll)
    // Unthrottled: onScroll often runs on the wheel event, before the content
    // moves, and then drops the scroll events that follow within its window.
    // Without this, a quick scroll away while paused leaves the control hidden
    // even though the active sentence has left the view.
    this.scrollContainer.addEventListener(
      'scroll',
      this.toggleScrollToCurrentPositionButton
    )
  }

  removeEventListeners() {
    this.scrollContainer.removeEventListener('wheel', this.onScroll)
    this.scrollContainer.removeEventListener('scroll', this.onScroll)
    this.scrollContainer.removeEventListener(
      'scroll',
      this.toggleScrollToCurrentPositionButton
    )
  }

  highlightSavedPosition() {
    // Priority 1: player is already mid-session (playing or paused) — seek to exact playhead position.
    // Priority 2: player hasn't started yet — fall back to the server-saved position from last session.
    let timeToScrollTo

    const currentTime = this.player.currentTime()
    if (currentTime > 0) {
      timeToScrollTo = currentTime
    } else {
      const playerEl = document.querySelector(this.playerSelectorValue)
      if (!playerEl) return

      const raw = playerEl.dataset.position
      if (!raw) return

      try {
        const { position_sec } = JSON.parse(raw)
        // scroll 5 s earlier so you can catch up to where you left off
        if (position_sec > 10) timeToScrollTo = position_sec - 5
      } catch (_) {
        return
      }
    }

    if (!timeToScrollTo) return

    const sentence =
      this.findSentenceByTime(timeToScrollTo) ||
      this.findNearestSentence(timeToScrollTo)

    if (!sentence) return

    // Suppress autoScroll inside highlightSentence — it races with
    // scrollToSentenceWhenVisible and the two animated scrolls interfere.
    this.shouldAutoScroll = false
    this.highlightSentence(sentence)
    this.shouldAutoScroll = true
    // highlightSentence evaluated the control while following was off above.
    this.toggleScrollToCurrentPositionButton()

    this.scrollToSentenceWhenVisible(sentence)
  }

  // The transcript is lazy-loaded via AJAX, so even after transcriptContainerTargetConnected
  // fires, the browser may not have finished layout yet. And if the transcript tab isn't the
  // default, the scroll container has zero height. Poll until the container is ready.
  scrollToSentenceWhenVisible(sentence, attempts = 0) {
    if (attempts > 20) return // give up after ~2s

    const ready =
      this.scrollContainer &&
      this.scrollContainer.offsetHeight > 0 &&
      sentence.offsetHeight > 0

    if (ready && !this.isSentenceInView(sentence)) {
      this.scrollToSentence(sentence)
    } else if (!ready) {
      this.savedPositionScrollTimeout = setTimeout(
        () => this.scrollToSentenceWhenVisible(sentence, attempts + 1),
        100
      )
    }
  }

  // Jump to

  jumpToSpan(span) {
    this.shouldAutoScroll = true
    setPlayerCurrentTime(this.player, span.dataset.start, {
      playAfterSeek: true,
    })
    this.highlightSentence(span)
    if (this.searchActive) {
      this.searchInputTarget.focus()
    }
  }

  jumpToSelection(e) {
    if (this.currentSelection) {
      this.jumpToSpan(this.currentSelection.startNode)
    }
  }

  bookmarkNextSentence() {
    if (this.currentBookmark) {
      // Bookmark is in a section, find the first paragraph, then first sentence
      const section = this.currentBookmark.closest('.section')
      if (!section) return null

      const paragraph = section.querySelector('.paragraph')
      if (!paragraph) return null

      return paragraph.querySelector('.js-sentence')
    }
  }

  jumpToBookmark(e) {
    if (this.currentBookmark) {
      const sentence = this.bookmarkNextSentence()
      if (sentence) this.jumpToSpan(sentence)
    }
  }

  jumpToBookmarkInTranscript(bookmark) {
    this.deactivateBookmarksOnly()
    requestAnimationFrame(() => this.scrollToSentence(bookmark))
  }

  jumpToSentenceInTranscript(sentence) {
    this.deactivateHighlightsOnly()
    requestAnimationFrame(() =>
      sentence.scrollIntoView({ behavior: 'smooth', block: 'center' })
    )
  }

  onScroll = throttle(
    100,
    (e) => {
      // Don't interfere with programmatic scrolling
      if (this.isScrolling) return

      this.disengageAutoScroll()
      this.updateHeaderVisibility()
      this.toggleScrollToCurrentPositionButton()
    },
    { noTrailing: true }
  )

  disengageAutoScroll() {
    this.shouldAutoScroll = false
    clearTimeout(this.shouldAutoScrollTimeout)
    this.shouldAutoScrollTimeout = setTimeout(() => {
      this.shouldAutoScroll = true
      // If playback is paused, no further timeupdate will come along to
      // recompute this - without this call the control can stay stuck
      // visible indefinitely even though auto-follow is engaged again.
      this.toggleScrollToCurrentPositionButton()
    }, 15000)
  }

  updateHeaderVisibility() {
    if (!this.hasHeaderTarget || !this.scrollContainer) return

    const scrollTop = Math.max(this.scrollContainer.scrollTop || 0, 0)
    const scrollDelta = scrollTop - this.lastScrollTop
    const now = Date.now()
    const direction = scrollDelta > 4 ? 'down' : scrollDelta < -4 ? 'up' : null

    if (this.searchActive || scrollTop <= 8) {
      this.showHeader(scrollTop)
      this.headerVisibilityDirection = null
      this.headerDirectionAnchorScrollTop = scrollTop
    } else if (now >= this.headerToggleLockUntil && direction) {
      if (direction != this.headerVisibilityDirection) {
        this.headerVisibilityDirection = direction
        this.headerDirectionAnchorScrollTop = scrollTop
      } else if (this.transcriptHeaderHidden) {
        if (
          direction == 'up' &&
          this.headerDirectionAnchorScrollTop - scrollTop >= 48
        ) {
          this.showHeader(scrollTop)
        }
      } else if (
        direction == 'down' &&
        scrollTop - this.headerDirectionAnchorScrollTop >= 48
      ) {
        this.hideHeader(scrollTop)
      }
    }

    this.lastScrollTop = scrollTop
  }

  hideHeader(scrollTop = this.lastScrollTop) {
    if (
      this.transcriptHeaderHidden ||
      this.searchActive ||
      !this.hasHeaderTarget ||
      this.element.classList.contains('bookmarks-only') ||
      this.element.classList.contains('highlights-only')
    )
      return

    this.headerTarget.classList.add('transcript-header--hidden')
    this.transcriptHeaderHidden = true
    this.headerVisibilityDirection = 'down'
    this.headerDirectionAnchorScrollTop = scrollTop
    this.headerToggleLockUntil = Date.now() + 250
  }

  showHeader(scrollTop = this.lastScrollTop) {
    if (!this.transcriptHeaderHidden || !this.hasHeaderTarget) return

    this.headerTarget.classList.remove('transcript-header--hidden')
    this.transcriptHeaderHidden = false
    this.headerVisibilityDirection = 'up'
    this.headerDirectionAnchorScrollTop = scrollTop
    this.headerToggleLockUntil = Date.now() + 250
  }

  scrollToCurrentPosition = () => {
    if (!this.previouslyHighlightedSentence) return

    this.shouldAutoScroll = true
    clearTimeout(this.shouldAutoScrollTimeout)
    // Hide right away rather than via toggleScrollToCurrentPositionButton():
    // scrollToSentence is debounced, so isScrolling isn't set yet, and during
    // search following stays off, so neither gate would hide it here.
    this.scrollToCurrentPositionButtonTarget.classList.add('hidden')
    this.scrollToSentence(this.previouslyHighlightedSentence)
  }

  // Helper method for binary search by time
  findSentenceByTime(time) {
    if (!this.sentences || this.sentences.length === 0) return null

    let left = 0
    let right = this.sentences.length - 1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)
      const sentence = this.sentences[mid]

      if (time >= sentence.start && time < sentence.end) {
        return sentence.el
      } else if (time < sentence.start) {
        right = mid - 1
      } else {
        left = mid + 1
      }
    }

    return null // No matching sentence found
  }

  // findSentenceByTime returns null when the time falls in a gap between sentences
  // or past the last sentence. For saved position restore we still want the closest one.
  findNearestSentence(time) {
    if (!this.sentences?.length) return null

    return this.sentences.reduce((nearest, s) =>
      Math.abs(s.start - time) < Math.abs(nearest.start - time) ? s : nearest
    ).el
  }

  onTimeUpdate = () => {
    const time = this.player.currentTime()
    const sentenceToHighlight = this.findSentenceByTime(time)
    this.highlightSentence(sentenceToHighlight)
  }

  highlightSentence = (sentence) => {
    if (!sentence) return
    if (this.previouslyHighlightedSentence !== sentence) {
      this.previouslyHighlightedSentence?.classList?.remove(
        ...this.highlightedSentenceClasses
      )
    }
    sentence?.classList?.add(...this.highlightedSentenceClasses)
    this.previouslyHighlightedSentence = sentence
    this.autoScroll(sentence)
    this.toggleScrollToCurrentPositionButton()
  }

  toggleScrollToCurrentPositionButton = () => {
    // Can fire after disconnect() nulls this out - an in-flight
    // scrollToSentence animation's promise isn't cancellable, so its .then()
    // still runs and calls back in here even once torn down.
    if (!this.scrollContainer) return

    const { hidden, isAbove } = returnControlState({
      shouldAutoScroll: this.shouldAutoScroll,
      isScrolling: this.isScrolling,
      sentenceRect: this.previouslyHighlightedSentence?.getBoundingClientRect(),
      containerRect: this.scrollContainer.getBoundingClientRect(),
    })

    this.scrollToCurrentPositionIconTarget.classList.toggle(
      'fa-rotate-180',
      isAbove
    )

    this.scrollToCurrentPositionButtonTarget.classList.toggle('hidden', hidden)
  }

  autoScroll = (sentence) => {
    const { scrollTop, clientHeight, scrollHeight } = this.scrollContainer
    if (
      !this.shouldAutoScroll ||
      isSentenceInFollowView(
        sentence.getBoundingClientRect(),
        this.scrollContainer.getBoundingClientRect(),
        { atScrollEnd: scrollTop + clientHeight >= scrollHeight - 1 }
      )
    )
      return

    this.scrollToSentence(sentence)
  }

  scrollToSentence = (sentence) => {
    // Debounced, so this can run after disconnect() - without a container
    // animated-scroll-to would scroll the window instead.
    if (!this.scrollContainer) return

    // The branching below depends on two animated-scroll-to behaviors that
    // aren't obvious from this call site alone: (1) starting a second call on
    // the same elementToScroll cancels an older still-running one, resolving
    // the older call's promise false while the newer one continues and settles
    // true; (2) a cancelOnUserAction cancel (e.g. a wheel event mid-scroll)
    // also resolves false. The controller tests run both against the installed
    // library, so a version bump that changes them fails there.
    const token = ++this.scrollToken
    this.isScrolling = true
    animateScrollTo(sentence, {
      cancelOnUserAction: true,
      elementToScroll: this.scrollContainer,
      maxDuration: 300,
      verticalOffset: -AUTO_FOLLOW_TOP_OFFSET,
    })
      .then((completed) => {
        // A newer scrollToSentence call superseded this one (animated-scroll-to
        // cancels any older animation on the same element) - let its own
        // completion own isScrolling/the button instead.
        if (token !== this.scrollToken) return

        if (completed) {
          // animated-scroll-to resolves in the same frame as its last scroll
          // write, and the browser dispatches that write's scroll event in the
          // next frame, before animation-frame callbacks. Settling one frame
          // later keeps that event programmatic, so onScroll doesn't mistake it
          // for the member scrolling away.
          requestAnimationFrame(() => {
            if (token !== this.scrollToken) return

            this.isScrolling = false
            this.toggleScrollToCurrentPositionButton()
          })
        } else {
          // cancelOnUserAction cancelled this scroll - the member took over.
          this.isScrolling = false
          this.disengageAutoScroll()
          this.toggleScrollToCurrentPositionButton()
        }
      })
      .catch(() => {
        // animated-scroll-to rejected (e.g. the sentence was detached
        // mid-animation) or the handler above threw. Never leave isScrolling
        // stuck on, or onScroll would ignore the member for good.
        if (token !== this.scrollToken) return

        this.isScrolling = false
        this.toggleScrollToCurrentPositionButton()
      })
  }

  isSentenceInView = (sentence) => {
    return inViewPortOfContainer(sentence, this.scrollContainer)
  }

  //
  // Higlights-Only
  //

  toggleHighlightsOnly() {
    const isActive = this.element.classList.contains('highlights-only')
    if (isActive) {
      this.deactivateHighlightsOnly()
    } else {
      this.activateHighlightsOnly()
    }
  }

  activateHighlightsOnly() {
    this.deactivateSearch()
    this.deactivateBookmarksOnly()
    this.element.classList.add('highlights-only')
    this.highlightsOnlyButtonTarget.classList.add('t-btn-active')
    this.highlightsOnlyButtonTarget.setAttribute('aria-pressed', 'true')
    this.showHeader()
    this.insertHighlightBreakMarkers()
  }

  deactivateHighlightsOnly() {
    this.element.classList.remove('highlights-only')
    this.highlightsOnlyButtonTarget.classList.remove('t-btn-active')
    this.highlightsOnlyButtonTarget.setAttribute('aria-pressed', 'false')
    this.removeHighlightBreakMarkers()
  }

  // Break marker between visible chunks where un-highlighted content has been collapsed.
  // Skipped when the gap is small or the chunks are in different sections (section
  // headers / bookmarks already act as the divider there).
  static HIGHLIGHT_BREAK_MIN_SECONDS = 10

  insertHighlightBreakMarkers() {
    this.removeHighlightBreakMarkers()
    if (!this.hasBreakMarkerTemplateTarget) return

    const sentences = Array.from(
      this.transcriptContainerTarget.querySelectorAll('.js-sentence')
    )
    const visible = sentences.filter((s) => this.isVisibleInHighlightsOnly(s))
    if (visible.length < 2) return

    const chunks = []
    let cur = null
    for (const s of visible) {
      const idx = Number(s.dataset.index)
      if (cur && idx === cur.lastIndex + 1) {
        cur.last = s
        cur.lastIndex = idx
      } else {
        if (cur) chunks.push(cur)
        cur = { first: s, last: s, lastIndex: idx }
      }
    }
    chunks.push(cur)

    const minGap = this.constructor.HIGHLIGHT_BREAK_MIN_SECONDS
    for (let i = 1; i < chunks.length; i++) {
      const prev = chunks[i - 1]
      const next = chunks[i]
      const fromSec = Number(prev.last.dataset.end)
      const toSec = Number(next.first.dataset.start)
      if (toSec - fromSec < minGap) continue
      const prevSection = prev.last.closest('.section')
      const nextSection = next.first.closest('.section')
      if (prevSection !== nextSection) continue // bookmark header is the divider
      this.insertBreakMarker(prev.last, next.first)
    }
  }

  removeHighlightBreakMarkers() {
    this.transcriptContainerTarget
      .querySelectorAll('.transcript-break')
      .forEach((el) => el.remove())
  }

  isVisibleInHighlightsOnly(sentence) {
    if (sentence.classList.contains('sentence--highlight')) return true
    const next = sentence.nextElementSibling
    const prev = sentence.previousElementSibling
    return (
      next?.classList.contains('sentence--highlight') ||
      prev?.classList.contains('sentence--highlight')
    )
  }

  insertBreakMarker(prevSentence, nextSentence) {
    const breakEl = this.breakMarkerTemplateTarget.content
      .cloneNode(true)
      .querySelector('.transcript-break')
    breakEl.querySelector('.transcript-break__time').textContent =
      this.formatBreakTime(nextSentence.dataset.start)
    const prevParagraph = prevSentence.closest('.paragraph')
    const nextParagraph = nextSentence.closest('.paragraph')
    if (prevParagraph === nextParagraph) {
      nextSentence.parentNode.insertBefore(breakEl, nextSentence)
    } else {
      nextParagraph.parentNode.insertBefore(breakEl, nextParagraph)
    }
  }

  formatBreakTime(seconds) {
    const total = Math.floor(Number(seconds) || 0)
    const h = Math.floor(total / 3600)
    const m = Math.floor((total % 3600) / 60)
    const s = total % 60
    const pad = (n) => String(n).padStart(2, '0')
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
  }

  //
  // Bookmarks-Only
  //

  toggleBookmarksOnly() {
    const isActive = this.element.classList.contains('bookmarks-only')
    if (isActive) {
      this.deactivateBookmarksOnly()
    } else {
      this.activateBookmarksOnly()
    }
  }

  activateBookmarksOnly() {
    this.deactivateSearch()
    this.deactivateHighlightsOnly()
    this.element.classList.add('bookmarks-only')
    this.bookmarksOnlyButtonTarget.classList.add('t-btn-active')
    this.bookmarksOnlyButtonTarget.setAttribute('aria-pressed', 'true')
    this.showHeader()
  }

  deactivateBookmarksOnly() {
    this.element.classList.remove('bookmarks-only')
    this.bookmarksOnlyButtonTarget.classList.remove('t-btn-active')
    this.bookmarksOnlyButtonTarget.setAttribute('aria-pressed', 'false')
  }

  //
  // Search
  //

  toggleSearch() {
    if (this.alwaysSearchValue) {
      this.searchInputTarget.focus()
      return
    }
    const isActive =
      !this.searchInputContainerTarget.classList.contains('hidden')
    if (isActive) {
      this.deactivateSearch()
    } else {
      this.activateSearch()
    }
  }

  activateSearch() {
    this.deactivateHighlightsOnly()
    this.deactivateBookmarksOnly()
    this.searchActive = true
    this.showHeader()
    this.element.classList.add('search-active')
    this.buttonsTarget.classList.add('hidden')
    this.searchInputContainerTarget.classList.remove('hidden')
    this.searchInputTarget.focus()
    this.toggleScrollToCurrentPositionButton()
  }

  deactivateSearch() {
    // The permanently-open bar has nothing to hide — Esc just clears the query.
    if (this.alwaysSearchValue) {
      this.clearSearch()
      return
    }
    this.searchInputTarget.value = ''
    this.searchActive = false
    this.numMatches = 0
    this.searchCountTarget.innerText = ''
    this.element.classList.remove('search-active')
    this.removeHighlight()
    this.buttonsTarget.classList.remove('hidden')
    this.searchInputContainerTarget.classList.add('hidden')
    this.searchNavigationTarget.classList.add('hidden')
    this.clearSearchButtonTarget.classList.add('hidden')
    this.toggleScrollToCurrentPositionButton()
  }

  clearSearch() {
    this.searchInputTarget.value = ''
    this.highlightedMatchNum = 0
    this.numMatches = 0
    this.searchCountTarget.innerText = ''
    this.termLower = ''
    this.removeHighlight()
    this.clearSearchButtonTarget.classList.add('hidden')
    this.searchInputTarget.focus()
  }

  focusSearch() {
    // Show search if it's not visible
    if (this.searchActive) {
      this.searchInputTarget.focus()
    } else {
      this.activateSearch()
    }
  }

  search() {
    this.highlightedMatchNum = 0
    this.doSearch()
  }

  // Don't debounce this. We want instant
  toggleClearSearch() {
    this.clearSearchButtonTarget.classList.toggle(
      'hidden',
      this.searchInputTarget.value.length === 0
    )
  }

  doSearch() {
    this.termLower = this.searchInputTarget.value.toLowerCase()

    this.removeHighlight()

    if (this.termLower.length < 3) {
      this.numMatches = 0
      this.searchCountTarget.innerText = ''
      return
    }

    // Count how many matches
    var counter = 0
    this.withMatches(() => counter++)
    this.numMatches = counter

    // Highlight first match
    if (this.numMatches > 0) {
      this.highlightMatch(this.highlightedMatchNum)
    } else {
      this.searchCountTarget.innerText = this.noMatchesValue
    }
  }

  // Optionally supply a number, in which case we'll only call for that match number
  // First match is 0
  withMatches(callback, number) {
    var counter = 0
    const spans = this.transcriptContainerTarget.querySelectorAll(
      'span.js-sentence-text'
    )
    for (const span of spans) {
      if (span.innerText.toLowerCase().includes(this.termLower)) {
        if (undefined === number || number === counter) {
          callback(span)
          if (number === counter) break
        }
        counter++
      }
    }
  }

  highlightMatch(number) {
    this.removeHighlight()
    const classes = this.searchMatchClasses.join(' ')
    this.withMatches((span) => {
      span.innerHTML = span.innerHTML.replace(
        new RegExp(`(${this.termLower})`, 'gi'),
        `<span class="${classes} --tr-match">$1</span>`
      )
      this.scrollToSentence(span)
      this.currentSearchHighlight = span
    }, number)
    this.highlightedMatchNum = number
    this.searchCountTarget.innerText = `${number + 1}/${this.numMatches}`
    this.searchNavigationTarget.classList.toggle('hidden', this.numMatches <= 1)
  }

  highlightNextMatch() {
    this.navigateMatch(1)
  }

  highlightPrevMatch() {
    this.navigateMatch(-1)
  }

  navigateMatch(direction) {
    if (this.numMatches < 2) return
    let nextIndex = this.highlightedMatchNum + direction
    if (nextIndex < 0) {
      nextIndex = this.numMatches - 1
    } else if (nextIndex >= this.numMatches) {
      nextIndex = 0
    }
    this.highlightMatch(nextIndex)
  }

  playHighlightedMatch() {
    if (this.currentSearchHighlight) {
      this.jumpToSpan(this.currentSearchHighlight)
    }
  }

  removeHighlight() {
    this.transcriptContainerTarget
      .querySelectorAll('.--tr-match')
      .forEach((span) => {
        const textNode = document.createTextNode(span.textContent)
        span.replaceWith(textNode)
      })
    this.searchCountTarget.innerText = ''
    this.currentSearchHighlight = null
    this.searchNavigationTarget.classList.add('hidden')
  }

  // Selection

  preventContextMenu(event) {
    if (event.sourceCapabilities?.firesTouchEvents) event.preventDefault()
  }

  onTouchStart(event) {
    const selection = window.getSelection()
    this._previousSelectionNodes = {
      anchor: selection.anchorNode,
      extent: selection.extentNode,
    }

    const touch = event.touches?.[0]
    this._touchStartY = touch?.clientY ?? null

    if (!touch) return

    const sentence = event.target.closest('.js-sentence')
    if (!sentence) return

    this._touchStartPos = { x: touch.clientX, y: touch.clientY }
    this._touchMoved = false

    clearTimeout(this._longPressTimer)
    this._longPressTimer = setTimeout(() => {
      this._longPressTimer = null
      this._startHandleSelection(sentence)
    }, 400)
  }

  onTouchMove(event) {
    if (this._activeHandle) return

    const touch = event.touches?.[0]
    if (!touch) return

    if (this._longPressTimer && this._touchStartPos) {
      const dx = touch.clientX - this._touchStartPos.x
      const dy = touch.clientY - this._touchStartPos.y
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        clearTimeout(this._longPressTimer)
        this._longPressTimer = null
        this._touchMoved = true
      }
    }
  }

  // Selection handles — native-like drag handles for extending selection on touch

  _startHandleSelection(sentence, { vibrate = true } = {}) {
    if (vibrate) haptics.tick()
    this.clearSelection()

    const index = Number(sentence.dataset.index)
    this.currentSelection = {
      startIndex: index,
      endIndex: index,
      startSec: sentence.dataset.start,
      endSec: sentence.dataset.end,
      startNode: sentence,
      endNode: sentence,
    }
    sentence.classList.add('sentence--selected')

    this._createHandles()
    this._positionHandles()
  }

  _createHandles() {
    if (!this.hasSelectionHandleTemplateTarget) return

    const container = this.transcriptContainerTarget.querySelector(
      '.transcript-paragraphs-container'
    )
    if (!container) return

    document.addEventListener('touchmove', this._onHandleTouchMove, {
      passive: false,
    })

    const startEl = this.selectionHandleTemplateTarget.content
      .cloneNode(true)
      .querySelector('.transcript-selection-handle')
    const endEl = this.selectionHandleTemplateTarget.content
      .cloneNode(true)
      .querySelector('.transcript-selection-handle')

    startEl.dataset.handleType = 'start'
    endEl.dataset.handleType = 'end'

    startEl.addEventListener('touchstart', this._onHandleTouchStart, {
      passive: false,
    })
    endEl.addEventListener('touchstart', this._onHandleTouchStart, {
      passive: false,
    })

    container.appendChild(startEl)
    container.appendChild(endEl)

    this._handles = { startEl, endEl }
  }

  _removeHandles() {
    if (!this._handles) return
    this._handles.startEl.removeEventListener(
      'touchstart',
      this._onHandleTouchStart
    )
    this._handles.endEl.removeEventListener(
      'touchstart',
      this._onHandleTouchStart
    )
    this._handles.startEl.remove()
    this._handles.endEl.remove()
    this._handles = null
    this._stopAutoScroll()
    document.removeEventListener('touchmove', this._onHandleTouchMove)
  }

  _positionHandles() {
    if (!this._handles || !this.currentSelection) return

    const container = this.transcriptContainerTarget.querySelector(
      '.transcript-paragraphs-container'
    )
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const { startNode, endNode } = this.currentSelection

    const startRects = startNode.getClientRects()
    const endRects = endNode.getClientRects()
    if (!startRects.length || !endRects.length) return

    const startRect = startRects[0]
    const endRect = endRects[endRects.length - 1]

    const startTop = startRect.top - containerRect.top + container.scrollTop
    this._handles.startEl.style.left = `${
      startRect.left - containerRect.left
    }px`
    this._handles.startEl.style.top = `${startTop}px`
    this._handles.startEl.querySelector(
      '.transcript-selection-handle__line'
    ).style.height = `${startRect.height}px`

    const endTop = endRect.top - containerRect.top + container.scrollTop
    this._handles.endEl.style.left = `${endRect.right - containerRect.left}px`
    this._handles.endEl.style.top = `${endTop}px`
    this._handles.endEl.querySelector(
      '.transcript-selection-handle__line'
    ).style.height = `${endRect.height}px`
  }

  _onHandleTouchStart = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget
    this._activeHandle = handle.dataset.handleType

    this._cachedHighlightTippy?.destroy()
    this._cachedHighlightTippy = null

    document.addEventListener('touchend', this._onHandleTouchEnd, {
      once: true,
    })
    document.addEventListener('touchcancel', this._onHandleTouchEnd, {
      once: true,
    })
  }

  _onHandleTouchMove = (event) => {
    if (!this._activeHandle) return
    event.preventDefault()

    const touch = event.touches?.[0]
    if (!touch) return

    this._lastTouchClientY = touch.clientY
    this._lastTouchClientX = touch.clientX

    const sentence = this._sentenceFromPoint(touch.clientX, touch.clientY)
    if (sentence) {
      this._updateSelectionFromHandle(sentence)
    }

    this._handleAutoScroll(touch.clientY)
  }

  _sentenceFromPoint(x, y) {
    // Hide handles so elementFromPoint hits the sentence underneath
    if (this._handles) {
      this._handles.startEl.style.pointerEvents = 'none'
      this._handles.endEl.style.pointerEvents = 'none'
    }

    const el = document.elementFromPoint(x, y)
    let sentence = el?.closest('.js-sentence')

    if (this._handles) {
      this._handles.startEl.style.pointerEvents = ''
      this._handles.endEl.style.pointerEvents = ''
    }

    // When finger is in a gap (paragraph margin, timestamp, section boundary),
    // find the nearest sentence by vertical position
    if (!sentence) {
      sentence = this._nearestSentenceByY(y)
    }

    return sentence
  }

  _nearestSentenceByY(clientY) {
    let closest = null
    let closestDist = Infinity

    const sentences =
      this.transcriptContainerTarget.querySelectorAll('.js-sentence')
    for (const s of sentences) {
      const rect = s.getBoundingClientRect()
      const centerY = rect.top + rect.height / 2
      const dist = Math.abs(clientY - centerY)
      if (dist < closestDist) {
        closestDist = dist
        closest = s
      }
    }

    return closest
  }

  _updateSelectionFromHandle(sentence) {
    if (!this.currentSelection || !this.widgetInitialized) return
    const sentenceIndex = Number(sentence.dataset.index)
    let { startIndex, endIndex } = this.currentSelection

    if (this._activeHandle === 'start') {
      startIndex = Math.min(sentenceIndex, endIndex)
    } else {
      endIndex = Math.max(sentenceIndex, startIndex)
    }

    if (
      startIndex === this.currentSelection.startIndex &&
      endIndex === this.currentSelection.endIndex
    )
      return

    const startNode = this.transcriptContainerTarget.querySelector(
      `.js-sentence[data-index="${startIndex}"]`
    )
    const endNode = this.transcriptContainerTarget.querySelector(
      `.js-sentence[data-index="${endIndex}"]`
    )
    if (!startNode || !endNode) return

    this.transcriptContainerTarget
      .querySelectorAll('.sentence--selected')
      .forEach((el) => el.classList.remove('sentence--selected'))

    this.currentSelection = {
      startIndex,
      endIndex,
      startSec: startNode.dataset.start,
      endSec: endNode.dataset.end,
      startNode,
      endNode,
    }

    for (let i = startIndex; i <= endIndex; i++) {
      this.transcriptContainerTarget
        .querySelector(`.js-sentence[data-index="${i}"]`)
        ?.classList.add('sentence--selected')
    }

    this._positionHandles()
  }

  _onHandleTouchEnd = () => {
    document.removeEventListener('touchend', this._onHandleTouchEnd)
    document.removeEventListener('touchcancel', this._onHandleTouchEnd)
    this._activeHandle = null
    this._stopAutoScroll()

    if (!this.widgetInitialized) return

    if (this.currentSelection) this._showTooltipForCurrentSelection()
  }

  _handleAutoScroll(clientY) {
    if (!this.scrollContainer || !this.widgetInitialized) return
    const containerRect = this.scrollContainer.getBoundingClientRect()
    const edgeZone = 60
    const distFromTop = clientY - containerRect.top
    const distFromBottom = containerRect.bottom - clientY

    let scrollSpeed = 0
    if (distFromTop < edgeZone) {
      scrollSpeed = -Math.max(2, (edgeZone - distFromTop) / 3)
    } else if (distFromBottom < edgeZone) {
      scrollSpeed = Math.max(2, (edgeZone - distFromBottom) / 3)
    }

    if (scrollSpeed !== 0) {
      this._autoScrollSpeed = scrollSpeed
      if (!this._autoScrollRAF) this._autoScrollLoop()
    } else {
      this._stopAutoScroll()
    }
  }

  _autoScrollLoop = () => {
    if (!this._autoScrollSpeed || !this.scrollContainer) return

    this.scrollContainer.scrollTop += this._autoScrollSpeed

    if (this._lastTouchClientY) {
      const x =
        this._lastTouchClientX ||
        this.scrollContainer.getBoundingClientRect().left +
          this.scrollContainer.clientWidth / 2
      const sentence = this._sentenceFromPoint(x, this._lastTouchClientY)
      if (sentence) this._updateSelectionFromHandle(sentence)
    }

    this._autoScrollRAF = requestAnimationFrame(this._autoScrollLoop)
  }

  _stopAutoScroll() {
    if (this._autoScrollRAF) {
      cancelAnimationFrame(this._autoScrollRAF)
      this._autoScrollRAF = null
    }
    this._autoScrollSpeed = 0
  }

  _showTooltipForCurrentSelection() {
    if (!this.currentSelection) return

    const { startNode, endNode } = this.currentSelection
    const anchor = this.isSentenceInView(startNode) ? startNode : endNode

    this.showTooltip(anchor, this.userHighlightTooltipTarget)
    // showTooltip clears sentence--selected; restore it
    this.eachSentence((el) => el.classList.add('sentence--selected'))
  }

  previousSelectionNodeEqualToCurrentSelectionNodes() {
    const selection = window.getSelection()
    return (
      selection.anchorNode === this._previousSelectionNodes?.anchor &&
      selection.extentNode === this._previousSelectionNodes?.extent
    )
  }

  showSelectionTooltip(event) {
    if (event.target.closest('.tippy-box') || this.editingNode) {
      return
    }

    if (this._longPressTimer) {
      clearTimeout(this._longPressTimer)
      this._longPressTimer = null
    }

    // For sentence touches: _touchMoved is set in onTouchMove when finger moves >10px.
    // For bookmark/non-sentence touches: onTouchStart returns early so _touchMoved is never set;
    // wasScrolling fills that gap using the Y delta recorded before the early return.
    if (this._touchMoved) return

    if (event.target.closest('.transcript-selection-handle')) return

    const endY = event.changedTouches?.[0]?.clientY
    const wasScrolling =
      this._touchStartY != null &&
      endY !== undefined &&
      Math.abs(endY - this._touchStartY) > 10

    if (wasScrolling) return

    const bookmark = event.target.closest('.js-bookmark')
    if (bookmark) {
      if (this.element.classList.contains('bookmarks-only')) {
        this.jumpToBookmarkInTranscript(bookmark)
      } else {
        this.showBookmarkTooltip(bookmark)
      }
      return
    }

    if (this.element.classList.contains('highlights-only')) {
      const sentence = event.target.closest('.js-sentence')
      if (sentence) {
        this.jumpToSentenceInTranscript(sentence)
        return
      }
    }

    if (this._handles) {
      const sentence = event.target.closest('.js-sentence')
      if (sentence && sentence.classList.contains('sentence--selected')) {
        this._showTooltipForCurrentSelection()
        return
      }
      this.clearSelection()
    }

    setTimeout(() => {
      const selection = window.getSelection()

      const isTap =
        selection.isCollapsed ||
        this.previousSelectionNodeEqualToCurrentSelectionNodes()

      if (isTap) {
        const sentence = event.target.closest('.js-sentence')
        if (!sentence) return

        if (event.changedTouches) {
          // Touch tap: go straight to handles — avoids native line-select and
          // the accidental drag-to-select that comes with it.
          this._startHandleSelection(sentence, { vibrate: false })
          this._showTooltipForCurrentSelection()
        } else {
          // Mouse click: keep native selection + tooltip.
          selection.removeAllRanges()
          const range = document.createRange()
          range.selectNodeContents(sentence)
          selection.addRange(range)
          this.showSelectionActions(selection)
        }
        return
      }

      this.showSelectionActions(selection)
    }, 0)
  }

  maybeGetCurrentSelection() {
    const selection = window.getSelection()
    if (selection && !selection.isCollapsed) {
      return selection
    }
  }

  getSentenceFromSelectionNode(selectionNode) {
    if (selectionNode.matches && selectionNode.matches('.js-sentence')) {
      return selectionNode
    }

    if (selectionNode.closest) return selectionNode.closest('.js-sentence')

    return selectionNode.parentElement.closest('.js-sentence')
  }

  clearSelection() {
    this.transcriptContainerTarget
      .querySelectorAll('.sentence--selected, .bookmark--selected')
      .forEach((el) =>
        el.classList.remove('sentence--selected', 'bookmark--selected')
      )
    this.currentSelection = null
    this._cachedHighlightTippy?.destroy()
    this._cachedHighlightTippy = null
    this._removeHandles()
    document.getSelection().removeAllRanges()
  }

  eachSentence(callback) {
    for (
      let i = this.currentSelection.startIndex;
      i <= this.currentSelection.endIndex;
      i++
    ) {
      callback(
        this.transcriptContainerTarget.querySelector(
          `.js-sentence[data-index="${i}"]`
        )
      )
    }
  }

  // Tooltip

  showSelectionActions(selection) {
    const anchor = this.getSentenceFromSelectionNode(selection.anchorNode)
    const extent = this.getSentenceFromSelectionNode(selection.extentNode)

    let startIndex = Number(anchor.dataset.index)
    let endIndex = Number(extent.dataset.index)
    let startNode = extent
    let endNode = anchor

    // Handling backwards selections
    if (startIndex > endIndex) {
      const temp = startIndex
      startIndex = endIndex
      endIndex = temp
      startNode = extent
      endNode = anchor
    } else {
      startNode = anchor
      endNode = extent
    }

    this.currentSelection = {
      startIndex: startIndex,
      endIndex: endIndex,
      startSec: startNode.dataset.start,
      endSec: endNode.dataset.end,
      startNode: startNode,
      endNode: endNode,
    }

    // Clear native selection so iOS doesn't show its callout (Copy, Writing Tools, etc.)
    // over our custom tooltip. All data is already captured in this.currentSelection.
    selection.removeAllRanges()

    this.showTooltip(startNode, this.userHighlightTooltipTarget)

    // Visually mark selected sentences after showTooltip, which clears the previous selection
    this.eachSentence((el) => el.classList.add('sentence--selected'))
  }

  showBookmarkTooltip(bookmark) {
    this.currentBookmark = bookmark
    const content = this.bookmarkTooltipTarget
    const isUserBookmark = bookmark.classList.contains('js-user-bookmark')

    this.showTooltip(bookmark.querySelector('.js-bookmark-note'), content, {
      onCreate: (instance) => {
        instance.popper
          .querySelectorAll('.js-user-bookmark-action')
          .forEach((el) => {
            el.classList.toggle('hidden', !isUserBookmark)
          })
        instance.popper
          .querySelectorAll('.js-system-bookmark-action')
          .forEach((el) => {
            el.classList.toggle(
              'hidden',
              isUserBookmark || !bookmark.dataset.updateUrl
            )
          })
      },
    })

    bookmark
      .querySelector('.js-bookmark-note')
      ?.classList.add('bookmark--selected')
  }

  showTooltip(node, target, options = {}) {
    this.transcriptContainerTarget
      .querySelectorAll('.sentence--selected, .bookmark--selected')
      .forEach((el) =>
        el.classList.remove('sentence--selected', 'bookmark--selected')
      )
    this._cachedHighlightTippy?.destroy()

    this._cachedHighlightTippy = tippy(node, {
      getReferenceClientRect: () => node.getBoundingClientRect(),
      trigger: 'manual',
      placement: 'top',
      maxWidth: '300px',
      arrow: roundArrow,
      content: target.innerHTML,
      allowHTML: true,
      appendTo: this.transcriptContainerTarget,
      zIndex: 20,
      interactive: true,
      ...options,
    })

    this._cachedHighlightTippy.show()
    this.tippyChanged()

    const removeTooltip = () => {
      document.removeEventListener('selectionchange', removeTooltip)
    }

    document.addEventListener('selectionchange', removeTooltip)
  }

  tippyChanged() {
    if (this._cachedHighlightTippy?.popper) {
      const content =
        this._cachedHighlightTippy.popper.querySelector('.tippy-content')
      if (content) {
        $(content).changed()
      }
    }
  }

  // Highlight sentence

  toggleHighlight() {
    if (!this.currentSelection) {
      return
    }

    const startSec = parseFloat(this.currentSelection.startSec)
    const endSec = parseFloat(this.currentSelection.endSec)

    if (!this.isUserLoggedIn()) {
      this.toggleLocalHighlight(startSec, endSec)
      return
    }

    this.eachSentence((sentence) => {
      sentence.classList.add(...this.userHighlightClasses)
    })

    fetch(this.annotateUrlValue, {
      method: 'POST',
      body: JSON.stringify({
        ...getCsrfParamsAsObject(),
        start_pos: startSec,
        end_pos: endSec,
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        response.json().then((data) => {
          if (data.delete_url) {
            this.eachSentence((sentence) => {
              sentence.dataset.deleteUrl = data.delete_url
            })
          } else if (data.deleted) {
            // if we deleted more than one sentence, find all delete URLs, so we can fetch all the nodes
            // that need to have classes removed and also the data attribute
            let deletedURLs = []
            this.eachSentence((sentence) => {
              if (!deletedURLs.includes(sentence.dataset.deleteUrl)) {
                deletedURLs.push(sentence.dataset.deleteUrl)
              }
            })
            // Find the nodes to work on
            let unhighlightNodes = []
            deletedURLs.forEach((deletedURL) => {
              unhighlightNodes.push(
                ...this.transcriptContainerTarget.querySelectorAll(
                  `.js-sentence[data-delete-url="${deletedURL}"]`
                )
              )
            })

            // Remove the classes and the data attribute
            unhighlightNodes.forEach((sentence) => {
              sentence.classList.remove(...this.userHighlightClasses)
              sentence.dataset.deleteUrl = null
            })
          }
        })
      })
      .catch((error) => alert(error))
  }

  toggleLocalHighlight(startSec, endSec) {
    const highlights = this.getLocalHighlights()
    const existingIndex = highlights.findIndex(
      (h) => h.start_pos === startSec && h.end_pos === endSec
    )

    if (existingIndex >= 0) {
      highlights.splice(existingIndex, 1)
    } else {
      highlights.push({
        start_pos: startSec,
        end_pos: endSec,
        created_at: new Date().toISOString(),
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue,
      })
    }

    this.saveLocalHighlights(highlights)

    this.eachSentence((sentence) => {
      if (existingIndex >= 0) {
        sentence.classList.remove(...this.userHighlightClasses)
        sentence.dataset.deleteUrl = null
      } else {
        sentence.classList.add(...this.userHighlightClasses)
        sentence.dataset.deleteUrl = 'local'
      }
    })
  }

  // Edit sentence

  editSentence() {
    const sentence = this.transcriptContainerTarget.querySelector(
      `.js-sentence[data-index="${this.currentSelection.startIndex}"]`
    )
    this.startEdit(sentence)
  }

  // Bookmarks

  addUserBookmark() {
    if (!this.currentSelection) return

    const startNode = this.currentSelection.startNode
    const startSec = Number(this.currentSelection.startSec)

    const note = prompt('Enter bookmark name:')?.trim()
    if (!note) return

    this.clearSelection()

    fetch(this.annotateUrlValue, {
      method: 'POST',
      body: JSON.stringify({
        ...getCsrfParamsAsObject(),
        start_pos: startSec,
        note,
        context_type: this.contextTypeValue,
        context_id: this.contextIdValue,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to create bookmark')
        return response.json()
      })
      .then((data) => {
        if (data.delete_url) {
          this.splitParagraphAtSentence(startNode, note, {
            delete_url: data.delete_url,
            update_url: data.update_url,
            convert_url: data.convert_url,
            timestamp: data.timestamp,
          })
        }
      })
      .catch((error) => {
        console.error('Error creating bookmark:', error)
        alert('Failed to create bookmark')
      })
  }

  splitParagraphAtSentence(sentence, note, data) {
    const currentSection = sentence.closest('.section')
    if (!currentSection) return

    const currentParagraph = sentence.closest('.paragraph')
    if (!currentParagraph) return

    // Get all sentences in the current paragraph
    const allSentences = Array.from(
      currentParagraph.querySelectorAll('.js-sentence')
    )
    const sentenceIndex = allSentences.indexOf(sentence)

    if (sentenceIndex === -1) return

    const fragment = this.createBookmarkNode(note, data)
    const bookmarkNode = fragment.querySelector('.js-bookmark')

    // If this is already the first sentence, just add the bookmark at the top of the section
    if (sentenceIndex === 0) {
      currentSection.insertBefore(fragment, currentParagraph)
      setTimeout(() => $(currentSection).changed(), 0)
      return bookmarkNode
    }

    // Create a new section with bookmark and paragraph
    const newSection = document.createElement('div')
    newSection.className = 'section'

    // Add the bookmark
    newSection.appendChild(fragment)

    // Create new paragraph for the split sentences
    const newParagraph = document.createElement('div')
    newParagraph.className = 'paragraph'

    // Clone the timestamp
    const timestamp = currentParagraph.querySelector('.timestamp')
    if (timestamp) {
      newParagraph.appendChild(timestamp.cloneNode(true))
    }

    // Move sentences from the split point onwards to the new paragraph
    for (let i = sentenceIndex; i < allSentences.length; i++) {
      newParagraph.appendChild(allSentences[i])
    }

    newSection.appendChild(newParagraph)

    // Move all following paragraphs in the current section to the new section
    const allParagraphs = Array.from(
      currentSection.querySelectorAll('.paragraph')
    )
    const currentParagraphIndex = allParagraphs.indexOf(currentParagraph)
    for (let i = currentParagraphIndex + 1; i < allParagraphs.length; i++) {
      newSection.appendChild(allParagraphs[i])
    }

    // Insert the new section after the current one
    currentSection.insertAdjacentElement('afterend', newSection)
    setTimeout(() => $(newSection).changed(), 0)
    return bookmarkNode
  }

  createBookmarkNode(note, data) {
    const fragment = this.userBookmarkTemplateTarget.content.cloneNode(true)

    const bookmarkNode = fragment.querySelector('.js-bookmark')
    const noteNode = fragment.querySelector('.js-bookmark-note')
    const timestampNode = fragment.querySelector('.js-bookmark-timestamp')

    bookmarkNode.dataset.deleteUrl = data.delete_url
    bookmarkNode.dataset.updateUrl = data.update_url
    if (data.convert_url) bookmarkNode.dataset.convertUrl = data.convert_url
    noteNode.textContent = note
    timestampNode.textContent = data.timestamp

    return fragment
  }

  editBookmark(e) {
    if (!this.currentBookmark) {
      return
    }
    const noteNode = this.currentBookmark.querySelector('.js-bookmark-note')
    this.startEdit(noteNode)
  }

  mergeSectionAfterBookmarkDelete(bookmark) {
    const section = bookmark.closest('.section')
    if (!section) return

    // Check if this is the last bookmark in the section
    const bookmarksInSection = section.querySelectorAll('.js-bookmark')
    if (bookmarksInSection.length > 1) {
      // Section still has other bookmarks, just remove this one
      return
    }

    // This is the last bookmark in the section, so merge with previous section
    const previousSection = section.previousElementSibling
    if (previousSection && previousSection.classList.contains('section')) {
      // Move all paragraphs from this section to the previous section
      const paragraphs = Array.from(section.querySelectorAll('.paragraph'))
      paragraphs.forEach((paragraph) => {
        previousSection.appendChild(paragraph)
      })
      // Remove the now-empty section
      section.remove()
    } else {
      // No previous section, try next section
      const nextSection = section.nextElementSibling
      if (nextSection && nextSection.classList.contains('section')) {
        // Move all paragraphs from this section to the next section
        const paragraphs = Array.from(section.querySelectorAll('.paragraph'))
        const firstParagraphInNext = nextSection.querySelector('.paragraph')
        paragraphs.forEach((paragraph) => {
          nextSection.insertBefore(paragraph, firstParagraphInNext)
        })
        // Remove the now-empty section
        section.remove()
      } else {
        // This is the only section, just remove the bookmark
        // The section can stay even without bookmarks
      }
    }
  }

  makeSystemBookmark(e) {
    if (!this.currentBookmark) return

    const convertUrl = this.currentBookmark.dataset.convertUrl
    if (!convertUrl) return

    fetch(convertUrl, {
      method: 'POST',
      body: JSON.stringify({
        ...getCsrfParamsAsObject(),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          response.json().then((data) => {
            this.currentBookmark.classList.remove('js-user-bookmark')
            this.currentBookmark.dataset.deleteUrl = data.delete_url
            this.currentBookmark.dataset.updateUrl = data.update_url
            delete this.currentBookmark.dataset.convertUrl
            const icon = this.currentBookmark.querySelector('.fa-bookmark')
            if (icon) {
              icon.classList.remove('text-yellow-500')
              icon.classList.add('text-fg-muted')
            }
            this.clearSelection()
          })
        } else {
          alert('Failed to convert bookmark')
        }
      })
      .catch((error) => {
        console.error('Error converting bookmark:', error)
        alert(error)
      })
  }

  deleteBookmark(e) {
    const bookmark = this.currentBookmark
    if (!bookmark) return

    const message = e.params.confirmMessage
    const destroy = () => this.destroyBookmark(bookmark)
    window.dispatchEvent(
      new CustomEvent('confirm-modal:open', {
        detail: { message, callback: destroy },
      })
    )
  }

  destroyBookmark(bookmark) {
    if (bookmark.dataset.deleting) return
    bookmark.dataset.deleting = 'true'
    const deleteUrl = bookmark.dataset.deleteUrl

    fetch(deleteUrl, {
      method: 'DELETE',
      body: JSON.stringify({
        ...getCsrfParamsAsObject(),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.ok) {
          this.mergeSectionAfterBookmarkDelete(bookmark)
          bookmark.remove()
          if (this.currentBookmark === bookmark) {
            this.currentBookmark = null
            this.clearSelection()
          }
        } else {
          delete bookmark.dataset.deleting
          alert('Failed to delete bookmark')
        }
      })
      .catch((error) => {
        delete bookmark.dataset.deleting
        console.error('Error deleting bookmark:', error)
        alert(error)
      })
  }

  // Editing

  startEdit(node) {
    this.editingNode = node

    this.valueBeforeEditing = node.innerText
    node.contentEditable = true
    node.classList.add(...this.editingClasses)

    // 🔑 Store the stop function so we can remove it later
    const [_startClickOutside, stopClickOutside] = useClickOutside(this, {
      element: node,
    })
    this._stopClickOutside = stopClickOutside

    this.clearSelection()
    node.focus()

    const range = document.createRange()
    range.selectNodeContents(node)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(range)
  }

  saveEdited() {
    if (this.editingNode) {
      if (this.editingNode.classList.contains('js-sentence')) {
        this.saveEditedSentence()
        this.endEditing()
      } else {
        this.saveEditedBookmark()
      }
    }
  }

  saveEditedSentence() {
    $.sajax({
      url: this.updateTranscriptUrlValue,
      type: 'POST',
      data: {
        sentence: this.editingNode.innerText,
        start: this.editingNode.dataset.start,
        end: this.editingNode.dataset.end,
      },
      success: () => this.dispatch('sentence-saved'),
    })
  }

  saveEditedBookmark() {
    if (this._savingBookmark) return
    this._savingBookmark = true

    $.sajax({
      url: this.currentBookmark.dataset.updateUrl,
      type: 'PATCH',
      data: { note: this.editingNode.innerText },
      success: (data) => {
        this._savingBookmark = false
        if (data.success) this.endEditing()
      },
      complete: () => {
        this._savingBookmark = false
      },
    })
  }

  clickOutside() {
    // Auto-save when clicking outside instead of prompting to cancel
    this.saveEdited()
  }

  cancelEditing() {
    if (this.editingNode) {
      const changed = this.valueBeforeEditing !== this.editingNode.innerText
      if (!changed || confirm('Cancel without saving changes?')) {
        this.editingNode.innerText = this.valueBeforeEditing
        this.endEditing()
      }
    }
  }

  endEditing() {
    if (this.editingNode) {
      this.editingNode.contentEditable = false
      this.editingNode.classList.remove(...this.editingClasses)

      this.editingNode = null
      this.valueBeforeEditing = null
    }

    if (this._stopClickOutside) {
      this._stopClickOutside() // unbind any previously bound click outside
      this._stopClickOutside = null
    }

    this.clearSelection()
  }

  // Copy to clipboard

  copyLinkToSentence(event) {
    if (this.currentSelection) {
      this.copyLinkToSec(this.currentSelection.startSec)
      this.copiedTooltip(event)
    }
  }

  copyLinkToSec(sec) {
    const url = new URL(this.linkUrlValue || window.location.href)
    url.searchParams.set(`media_position_${this.assetIdValue}`, sec)
    navigator.clipboard.writeText(url.toString())
  }

  copyLinkToBookmark(event) {
    if (this.currentBookmark) {
      const sentence = this.bookmarkNextSentence()
      if (sentence) {
        this.copyLinkToSec(sentence.dataset.start)
        this.copiedTooltip(event)
      }
    }
  }

  getSelectedText() {
    if (!this.currentSelection) return ''

    const sentences = []
    for (
      let i = this.currentSelection.startIndex;
      i <= this.currentSelection.endIndex;
      i++
    ) {
      const el = this.transcriptContainerTarget.querySelector(
        `.js-sentence[data-index="${i}"]`
      )
      sentences.push(el?.textContent?.trim() ?? '')
    }
    return sentences.join(' ')
  }

  copyText(event) {
    const text = this.getSelectedText()
    if (!text) return

    navigator.clipboard.writeText(text)
    this.copiedTooltip(event)
  }

  quoteInAiChat() {
    const text = this.getSelectedText()
    if (!text) return

    const lessonTabs = this.element.closest(
      '[data-controller*="community--courses--lesson-tabs"]'
    )
    if (!lessonTabs) return

    const chatBotTab = lessonTabs.querySelector('[data-lesson-tab="chat_bot"]')
    if (chatBotTab && !chatBotTab.classList.contains('t-tab-active')) {
      chatBotTab.click()
    }

    this.clearSelection()

    // Defer until after the tab switch renders so the input is visible and focusable
    requestAnimationFrame(() => {
      document.dispatchEvent(
        new CustomEvent('chat-bot:quote-text', { detail: { text } })
      )
    })
  }

  copiedTooltip(event) {
    if (this.restoreClipboardIconCallback) {
      clearTimeout(this.copyToClipboardTimeout)
      this.restoreClipboardIconCallback()
      this.copyToClipboardTimeout = null
      this.restoreClipboardIconCallback = null
    }

    const button = event.currentTarget
    // We use this one, because this thing won't be updated when to update the tooltip'
    const orgTitle = button.getAttribute('data-content')

    const iconTag = button.querySelector('i')
    // We assume that we use 'fas' or 'far' instead of 'fa-solid' or 'fa-regular' or similar.
    const orgIconClass = Array.from(iconTag.classList).find((className) =>
      className.match(/^fa-\w+$/)
    )

    iconTag.classList.replace(orgIconClass, 'fa-check')
    this.updateTooltip(button, event.params.copiedText)

    this.restoreClipboardIconCallback = () => {
      iconTag.classList.replace('fa-check', orgIconClass)
      this.updateTooltip(button, orgTitle)
    }

    this.copyToClipboardTimeout = setTimeout(
      this.restoreClipboardIconCallback,
      2000
    )
  }

  updateTooltip(elm, tooltip) {
    elm.dispatchEvent(
      new CustomEvent('updateContent', {
        detail: { content: tooltip, show: true },
      })
    )
  }
}
